import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { listCatalogChecklist } from '~/api/catalog'
import { AppShell } from '~/components/layout/app-shell'
import { EmptyState } from '~/components/shared/empty-state'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import {
    EMPTY_EXPLORE_FILTERS,
    filterExploreItems,
    getExploreBreadcrumb,
    getExploreOptions,
} from './explore-model'
import { ExploreBreadcrumb } from './explore-breadcrumb'
import { ExploreDetailPanel } from './explore-detail-panel'
import { ExploreOptionColumn } from './explore-option-column'
import { ExploreResultColumn } from './explore-result-column'
import type {
    ExploreChecklistItem,
    ExploreFilters,
    ExploreLevel,
} from './types'

/** HACCP 체크리스트를 실제 적재 카탈로그 기준으로 탐색한다. */
export function ExplorePage() {
    const [items, setItems] = useState<ExploreChecklistItem[] | null>(null)
    const [error, setError] = useState('')
    const [filters, setFilters] = useState<ExploreFilters>(
        EMPTY_EXPLORE_FILTERS
    )
    const [selectedItemId, setSelectedItemId] = useState('')
    const [detailExpanded, setDetailExpanded] = useState(false)

    useEffect(() => {
        void loadChecklist()
    }, [])

    // 찾아보기는 내 사업장 프로필과 무관하게 카탈로그 '전체'를 탐색한다(스코프 미적용).
    const scopedItems = useMemo(() => items ?? [], [items])

    // 카탈로그 전체(인증·정기 쌍둥이 포함)를 그대로 보여준다. combo 미선택 첫 화면에서도
    // 인증·정기 두 단계가 모두 나오며, 아래 compareByCombo가 인증→정기 순으로 묶어
    // 번호가 섞이지 않게 정렬한다.
    const resultSourceItems = scopedItems

    const filteredItems = useMemo(
        () =>
            [...filterExploreItems(resultSourceItems, filters)].sort(
                compareByCombo
            ),
        [resultSourceItems, filters]
    )

    const selectedItem = useMemo(
        () => filteredItems.find((item) => item.id === selectedItemId) ?? null,
        [filteredItems, selectedItemId]
    )
    const hasSelectedItem = Boolean(selectedItem)

    const comboOptions = useMemo(
        () => getExploreOptions('combo', filters, scopedItems),
        [filters, scopedItems]
    )
    const industryOptions = useMemo(
        () => getExploreOptions('industry', filters, scopedItems),
        [filters, scopedItems]
    )
    const majorOptions = useMemo(
        () => getExploreOptions('major', filters, scopedItems),
        [filters, scopedItems]
    )
    const middleOptions = useMemo(
        () => getExploreOptions('middle', filters, scopedItems),
        [filters, scopedItems]
    )

    const breadcrumbParts = useMemo(
        () => getExploreBreadcrumb(filters),
        [filters]
    )

    const visibleColumns = useMemo(
        () =>
            [
                {
                    level: 'combo' as const,
                    value: filters.combo,
                    options: comboOptions,
                    visible: true,
                },
                {
                    level: 'industry' as const,
                    value: filters.industry,
                    options: industryOptions,
                    visible: Boolean(filters.combo),
                },
                {
                    level: 'major' as const,
                    value: filters.major,
                    options: majorOptions,
                    visible: Boolean(filters.industry),
                },
                {
                    level: 'middle' as const,
                    value: filters.middle,
                    options: middleOptions,
                    visible: Boolean(filters.major),
                },
            ].filter((column) => column.visible),
        [
            comboOptions,
            filters.combo,
            filters.industry,
            filters.major,
            filters.middle,
            industryOptions,
            majorOptions,
            middleOptions,
        ]
    )

    useEffect(() => {
        if (
            selectedItemId &&
            !filteredItems.some((item) => item.id === selectedItemId)
        ) {
            setSelectedItemId('')
        }
    }, [filteredItems, selectedItemId])

    async function loadChecklist() {
        setError('')
        try {
            const rows = await listCatalogChecklist()
            setItems(rows)
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : '체크리스트 카탈로그를 불러오지 못했습니다.'
            )
        }
    }

    const handleSelectLevel = (level: ExploreLevel, value: string) => {
        setFilters((current) => {
            const next = { ...current }

            if (
                (level === 'major' || level === 'middle') &&
                current[level] === value
            ) {
                next[level] = ''
            } else {
                next[level] = value
            }

            if (level === 'combo') {
                next.industry = ''
                next.major = ''
                next.middle = ''
            }
            if (level === 'industry') {
                next.major = ''
                next.middle = ''
            }
            if (level === 'major') {
                next.middle = ''
            }

            return next
        })
        setSelectedItemId('')
        setDetailExpanded(false)
    }

    const handleReset = () => {
        setFilters(EMPTY_EXPLORE_FILTERS)
        setSelectedItemId('')
        setDetailExpanded(false)
    }

    const handleSelectItem = (itemId: string) => {
        setSelectedItemId((current) => {
            const next = current === itemId ? '' : itemId
            if (next !== current) setDetailExpanded(false)
            return next
        })
    }

    return (
        <AppShell>
            <div className='h-full overflow-hidden'>
                <div className='mx-auto flex h-full w-full max-w-[1680px] flex-col gap-3 px-4 py-4 lg:px-8'>
                    <header className='flex shrink-0 flex-col gap-2 lg:flex-row lg:items-end lg:justify-between'>
                        <div>
                            <h1 className='text-foreground text-xl font-extrabold tracking-tight'>
                                HACCP 체크리스트 찾아보기
                            </h1>
                            <p className='text-muted-foreground mt-1 text-sm'>
                                실제 적재된 평가항목을 규모, 업종, 분류 기준으로
                                탐색합니다.
                            </p>
                        </div>
                        <Badge tone='info' className='w-fit'>
                            {items
                                ? `${scopedItems.length}개 적재 항목`
                                : '불러오는 중'}
                        </Badge>
                    </header>

                    {error ? (
                        <EmptyState
                            title='카탈로그를 불러오지 못했어요'
                            description={error}
                            action={
                                <Button onClick={() => void loadChecklist()}>
                                    다시 불러오기
                                </Button>
                            }
                        />
                    ) : items === null ? (
                        <div className='border-border bg-card rounded-sm border p-5'>
                            <p className='text-muted-foreground text-sm'>
                                체크리스트 카탈로그를 불러오는 중입니다.
                            </p>
                        </div>
                    ) : (
                        <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-hidden'>
                            <div className='shrink-0'>
                                <ExploreBreadcrumb
                                    parts={breadcrumbParts}
                                    count={filteredItems.length}
                                    onReset={handleReset}
                                />
                            </div>

                            <div
                                className={cn(
                                    'flex min-h-0 gap-2 overflow-x-auto overflow-y-hidden pb-1 transition-[flex-basis] duration-200 ease-out',
                                    hasSelectedItem && detailExpanded
                                        ? 'hidden'
                                        : '',
                                    hasSelectedItem
                                        ? 'flex-[0_1_38%]'
                                        : 'flex-[1_1_62%]'
                                )}>
                                {visibleColumns.map((column, index) => (
                                    <StepColumnFragment
                                        key={column.level}
                                        showDivider={index > 0}
                                        column={
                                            <ExploreOptionColumn
                                                level={column.level}
                                                value={column.value}
                                                options={column.options}
                                                onSelect={handleSelectLevel}
                                            />
                                        }
                                    />
                                ))}
                                <StepColumnFragment
                                    showDivider={visibleColumns.length > 0}
                                    column={
                                        <ExploreResultColumn
                                            items={filteredItems}
                                            selectedItemId={selectedItemId}
                                            query={filters.query}
                                            emptyMessage='조건에 맞는 체크리스트가 없습니다. 선택 범위나 검색어를 조정해 주세요.'
                                            className='min-w-[32rem] flex-[1_1_38rem]'
                                            onQueryChange={(query) =>
                                                setFilters((current) => ({
                                                    ...current,
                                                    query,
                                                }))
                                            }
                                            onSelectItem={handleSelectItem}
                                        />
                                    }
                                />
                            </div>

                            <ExploreDetailPanel
                                item={selectedItem}
                                expanded={detailExpanded}
                                onToggleExpanded={() =>
                                    setDetailExpanded((current) => !current)
                                }
                                className={cn(
                                    'min-h-0 transition-[flex-basis] duration-200 ease-out',
                                    detailExpanded
                                        ? 'flex-[1_1_auto]'
                                        : hasSelectedItem
                                          ? 'flex-[1_1_62%]'
                                          : 'flex-[0_0_10rem]'
                                )}
                            />
                        </div>
                    )}
                </div>
            </div>
        </AppShell>
    )
}

function StepColumnFragment({
    showDivider,
    column,
}: {
    showDivider: boolean
    column: ReactNode
}) {
    return (
        <>
            {showDivider ? (
                <div
                    className='text-muted-foreground grid shrink-0 place-items-center px-1 text-2xl font-bold'
                    aria-hidden='true'>
                    ›
                </div>
            ) : null}
            {column}
        </>
    )
}

// combo(규모·평가·단계) 순서로 정렬해, 전체 보기에서 인증/정기가 번호로 섞이지 않게 한다.
const SCALE_RANK: Record<string, number> = { general: 0, small: 1 }
const CLASS_RANK: Record<string, number> = { 선행요건: 0, HACCP관리: 1 }
const STAGE_RANK: Record<string, number> = { 인증: 0, 정기: 1 }

// HACCP관리(PART 4·6)는 원칙 섹션("1. HACCP팀" 등)마다 번호가 1부터 리셋된다.
// 대분류의 숫자 접두를 번호보다 먼저 정렬해야 같은 번호끼리 묶이지 않는다.
function sectionRank(majorCategory?: string | null): number {
    const n = parseInt(majorCategory ?? '', 10)
    return Number.isFinite(n) ? n : 0
}

function compareByCombo(
    a: ExploreChecklistItem,
    b: ExploreChecklistItem
): number {
    const sa = SCALE_RANK[a.scale] ?? 9
    const sb = SCALE_RANK[b.scale] ?? 9
    if (sa !== sb) return sa - sb
    const ca = CLASS_RANK[a.classification] ?? 9
    const cb = CLASS_RANK[b.classification] ?? 9
    if (ca !== cb) return ca - cb
    const ta = STAGE_RANK[a.assessmentType ?? ''] ?? 9
    const tb = STAGE_RANK[b.assessmentType ?? ''] ?? 9
    if (ta !== tb) return ta - tb
    const p = (a.part ?? '').localeCompare(b.part ?? '', 'ko')
    if (p !== 0) return p
    const ma = sectionRank(a.majorCategory)
    const mb = sectionRank(b.majorCategory)
    if (ma !== mb) return ma - mb
    return (a.itemNumber ?? 0) - (b.itemNumber ?? 0)
}
